import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  InputGroup,
  InputGroupText,
  Row,
  Col,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import Autocomplete from "react-google-autocomplete";
import { countries, abbreviations } from "helpers";
import RequiredField from "../../helpers/requiredField";
import BillingStateDropdown from "./billingStateDropdown";
import BillingTerritoryDropdown from "./billingTerritoryDropdown";

const FlagCA = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 24c6.627 0 12-5.373 12-12S18.627 0 12 0 0 5.373 0 12s5.373 12 12 12z"
      fill="#fff"
    />
    <path
      d="M24 12a12 12 0 00-6.783-10.81v21.62A12 12 0 0024 12zM0 12a12 12 0 006.783 10.81V1.19A12 12 0 000 12zM14.087 13.565l2.087-1.043L15.13 12v-1.043L13.043 12l1.044-2.087h-1.043L12 8.348l-1.043 1.565H9.912L10.956 12 8.87 10.957V12l-1.044.522 2.087 1.043-.522 1.044h2.087v1.565h1.044v-1.565h2.087l-.522-1.044z"
      fill="#D80027"
    />
  </svg>
);

const FlagUS = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 24c6.627 0 12-5.373 12-12S18.627 0 12 0 0 5.373 0 12s5.373 12 12 12z"
      fill="#fff"
    />
    <path
      d="M11.478 12H24c0-1.083-.144-2.132-.413-3.13H11.478V12zM11.478 5.74h10.76a12.062 12.062 0 00-2.768-3.131h-7.992v3.13zM12 24c2.824 0 5.42-.976 7.47-2.609H4.53A11.949 11.949 0 0012 24zM1.761 18.26H22.24a11.93 11.93 0 001.348-3.13H.414c.3 1.117.758 2.168 1.347 3.13z"
      fill="#D80027"
    />
    <path
      d="M5.559 1.874h1.093l-1.017.739.389 1.196-1.018-.74-1.017.74.336-1.033c-.896.746-1.68 1.62-2.328 2.594h.35l-.647.47c-.1.168-.197.34-.29.513l.31.951-.578-.419C1 7.19.868 7.5.75 7.817l.34 1.048h1.258l-1.017.74.388 1.195-1.017-.739-.61.443C.033 10.994 0 11.494 0 12h12V0C9.63 0 7.42.688 5.559 1.874zm.465 8.926l-1.018-.739-1.017.739.389-1.196-1.017-.739h1.257l.388-1.195.389 1.195h1.257l-1.017.74.389 1.195zm-.389-4.691l.389 1.195-1.018-.739-1.017.74.389-1.196-1.017-.74h1.257l.388-1.195.389 1.196h1.257l-1.017.739zm4.693 4.691l-1.017-.739-1.017.739.388-1.196-1.017-.739h1.257l.389-1.195.388 1.195h1.258l-1.018.74.389 1.195zm-.389-4.691l.389 1.195-1.017-.739-1.017.74.388-1.196-1.017-.74h1.257l.389-1.195.388 1.196h1.258l-1.018.739zm0-3.496l.389 1.196-1.017-.74-1.017.74.388-1.196-1.017-.739h1.257L9.311.678l.388 1.196h1.258l-1.018.739z"
      fill="#0052B4"
    />
  </svg>
);

const BillingStepAddress = ({
  googleApiKey,
  billing,
  setBilling,
  valid,
  handleStepBilling,
  allowCanadianPayments
}) => {
  const { displayName, countryName, address, city, zip } = billing;

  useEffect(() => {
    if (!allowCanadianPayments && billing.countryName !== "US") {
      setBilling(e => ({
        ...e,
        address: null,
        city: null,
        stateName: null,
        stateCode: null,
        zip: null,
        countryName: "US"
      }));
    }
  }, [billing, allowCanadianPayments, setBilling]);

  return (
    <div className="checkout__step-body">
      <FormGroup>
        <Label for="billingFullName">Billing Full Name</Label>
        <Input
          id="billingFullName"
          type="text"
          value={displayName || ""}
          onChange={e => {
            setBilling({
              ...billing,
              ...{
                displayName: e.target.value
              }
            });
          }}
          required
          invalid={!valid.displayName}
        />
        <RequiredField />
      </FormGroup>
      {allowCanadianPayments && (
        <FormGroup>
          <Label for="billingCountry">Billing Country</Label>
          <InputGroup>
            {countryName && (
              <InputGroupText>
                {countryName === "US" ? <FlagUS /> : <FlagCA />}
              </InputGroupText>
            )}
            <Input
              id="billingCountry"
              type="select"
              value={countryName || ""}
              onChange={e => {
                const next = {
                  address: null,
                  city: null,
                  stateName: null,
                  stateCode: null,
                  zip: null,
                  countryName: e.target.value
                };
                setBilling(current => ({
                  ...current,
                  ...next
                }));
              }}
              required
              invalid={!valid.countryName}
            >
              <option value="" hidden>
                Choose
              </option>
              {Object.entries(countries).map(([key, value]) => {
                return (
                  <option value={key} key={key}>
                    {value}
                  </option>
                );
              })}
            </Input>
          </InputGroup>
          <RequiredField />
        </FormGroup>
      )}
      {countryName && (
        <FormGroup>
          <Label for="billingAddress">Billing Address</Label>
          {/* <Input
            id="billingAddress"
            className="form-control"
            type="text"
            value={address || ""}
            onChange={e => {
              setBilling({
                ...billing,
                ...{
                  address: e.target.value
                }
              });
            }}
            required
            invalid={!valid.address}
          /> */}
          <Autocomplete
            id="billingAddress"
            className={`form-control ${!valid.address ? "is-invalid" : ""}`}
            type="text"
            value={address || ""}
            onChange={e => {
              setBilling({
                ...billing,
                ...{
                  address: e.target.value
                }
              });
            }}
            apiKey={googleApiKey}
            onPlaceSelected={place => {
              setBilling(e => ({
                ...e,
                address: place.formatted_address.split(",")[0],
                city: place.formatted_address.split(",")[1].replace(" ", ""),
                stateName:
                  abbreviations[
                    place.formatted_address.split(",")[2].slice(1, 3)
                  ],
                stateCode: place.formatted_address.split(",")[2].slice(1, 3),
                zip: place.formatted_address
                  .split(",")[2]
                  .slice(3)
                  .trim()
              }));
            }}
            options={{
              types: ["address"],
              componentRestrictions: { country: countryName }
            }}
            placeholder=""
            required
            no-cors="true"
          />
          <RequiredField />
        </FormGroup>
      )}
      {countryName && (
        <Row>
          <Col>
            <FormGroup>
              <Label for="billingCity" id="billingCityLabel">
                {countryName === "US" ? "Billing City" : "Billing Municipality"}
              </Label>
              <Input
                id="billingCity"
                type="text"
                value={city || ""}
                onChange={e => {
                  setBilling({
                    ...billing,
                    ...{
                      city: e.target.value
                    }
                  });
                }}
                required
                invalid={!valid.city}
              />
              <RequiredField />
            </FormGroup>
          </Col>
          <Col>
            <Label for="billingState" id="billingStateLabel">
              {countryName === "US"
                ? "Billing State"
                : "Billing Province/Territory"}
            </Label>
            {countryName && countryName === "US" ? (
              <BillingStateDropdown
                id="billingState"
                billing={billing}
                setBilling={setBilling}
                valid={valid}
                required
              />
            ) : (
              <BillingTerritoryDropdown
                id="billingState"
                billing={billing}
                setBilling={setBilling}
                valid={valid}
                required
              />
            )}
            {valid.stateName === false ||
              (billing.stateName === undefined && (
                <div id="billing-invalid-feedback">Required Field.</div>
              ))}
          </Col>
          <Col>
            <FormGroup>
              <Label for="billingZip" id="billingZipLabel">
                {countryName === "US"
                  ? "Billing ZIP Code"
                  : "Billing Postal Code"}
              </Label>
              {countryName && countryName === "US" ? (
                <Input
                  id="billingZip"
                  type="text"
                  pattern="\d*"
                  maxLength="5"
                  value={zip || ""}
                  onChange={e => {
                    setBilling({
                      ...billing,
                      ...{
                        zip: e.target.value.toUpperCase().trim()
                      }
                    });
                  }}
                  required
                  invalid={!valid.zip}
                />
              ) : (
                <Input
                  id="billingZip"
                  type="text"
                  pattern="(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$)"
                  maxLength="7"
                  value={zip || ""}
                  onChange={e => {
                    setBilling({
                      ...billing,
                      ...{
                        zip: e.target.value.toUpperCase().trim()
                      }
                    });
                  }}
                  required
                  invalid={!valid.zip}
                />
              )}
              <RequiredField />
            </FormGroup>
          </Col>
        </Row>
      )}
      <div className="checkout__step-continue">
        <Button
          block
          color="primary"
          id="billingStepAddress_saveAndContinue"
          onClick={handleStepBilling}
        >
          Save and Continue
        </Button>
      </div>
    </div>
  );
};

BillingStepAddress.propTypes = {
  googleApiKey: PropTypes.string,
  billing: PropTypes.shape({
    displayName: PropTypes.string,
    countryName: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    stateName: PropTypes.string,
    zip: PropTypes.string
  }).isRequired,
  setBilling: PropTypes.func.isRequired,
  valid: PropTypes.shape({
    displayName: PropTypes.bool,
    countryName: PropTypes.bool,
    address: PropTypes.bool,
    city: PropTypes.bool,
    stateName: PropTypes.bool,
    zip: PropTypes.bool
  }).isRequired,
  handleStepBilling: PropTypes.func.isRequired,
  allowCanadianPayments: PropTypes.bool.isRequired
};

BillingStepAddress.defaultProps = {
  googleApiKey: null
};

export default BillingStepAddress;
